// NEW: Add this function at the end of the file (around line 281)
interface ParsedError extends Error {
  status: number
  statusText: string
  url: string
  ok: boolean
  redirected: boolean
  type: string
  bodyUsed: boolean
  headers: Record<string, string>
  bodyData?: any // Parsed body (JSON or text)
  statusCode?: number // Alias for status (per existing code)
  isApiError: boolean
  bodyParseError?: string
}
/**
 * Returns the description of the HTTP status code
 * @param status - The HTTP status code
 * @returns The description of the HTTP status code
 */
const getHttpStatusDescription = (status: number | undefined): string => {
  if (!status) {
    return 'Unknown status'
  }
  const statusDescriptions: Record<number, string> = {
    // 1xx Informational
    100: 'Continue',
    101: 'Switching Protocols',
    102: 'Processing',
    103: 'Early Hints',

    // 2xx Success
    200: 'OK',
    201: 'Created',
    202: 'Accepted',
    203: 'Non-Authoritative Information',
    204: 'No Content',
    205: 'Reset Content',
    206: 'Partial Content',
    207: 'Multi-Status',
    208: 'Already Reported',
    226: 'IM Used',

    // 3xx Redirection
    300: 'Multiple Choices',
    301: 'Moved Permanently',
    302: 'Found',
    303: 'See Other',
    304: 'Not Modified',
    305: 'Use Proxy',
    307: 'Temporary Redirect',
    308: 'Permanent Redirect',

    // 4xx Client Errors
    400: 'Bad Request',
    401: 'Unauthorized',
    402: 'Payment Required',
    403: 'Forbidden',
    404: 'Not Found',
    405: 'Method Not Allowed',
    406: 'Not Acceptable',
    407: 'Proxy Authentication Required',
    408: 'Request Timeout',
    409: 'Conflict',
    410: 'Gone',
    411: 'Length Required',
    412: 'Precondition Failed',
    413: 'Payload Too Large',
    414: 'URI Too Long',
    415: 'Unsupported Media Type',
    416: 'Range Not Satisfiable',
    417: 'Expectation Failed',
    418: "I'm a teapot",
    421: 'Misdirected Request',
    422: 'Unprocessable Entity',
    423: 'Locked',
    424: 'Failed Dependency',
    425: 'Too Early',
    426: 'Upgrade Required',
    428: 'Precondition Required',
    429: 'Too Many Requests',
    431: 'Request Header Fields Too Large',
    451: 'Unavailable For Legal Reasons',

    // 5xx Server Errors
    500: 'Internal Server Error',
    501: 'Not Implemented',
    502: 'Bad Gateway',
    503: 'Service Unavailable',
    504: 'Gateway Timeout',
    505: 'HTTP Version Not Supported',
    506: 'Variant Also Negotiates',
    507: 'Insufficient Storage',
    508: 'Loop Detected',
    510: 'Not Extended',
    511: 'Network Authentication Required',
  }

  return statusDescriptions[status] || `Unknown status code: ${status}`
}

/**
 * Parses a fetch Response object into a custom Error, copying properties and creating a highlighted message.
 * @param response The fetch Response object to parse.
 * @returns A custom Error with copied properties and a custom message.
 */
export const parseResponseToError = async (
  response: Response
): Promise<ParsedError> => {
  try {
    console.log('🔄 parseResponseToError: Starting error parsing for response')

    // Create base error with custom message
    let customMessage = `HTTP Status: ${
      response?.status
    } - ${getHttpStatusDescription(response?.status)} \n URL: ${response?.url}`

    // Attempt to parse body for additional details (e.g., API error message)
    let bodyData: any = null
    let bodyParseError: string | null = null
    try {
      bodyData = await response?.clone()?.json() // Clone to avoid consuming original stream
      if (bodyData?.message) {
        customMessage += ` - Message: ${bodyData.message}`
      }
    } catch (jsonError) {
      try {
        bodyData = await response?.clone()?.text() // Fallback to text
      } catch (textError) {
        bodyParseError = 'Failed to parse response body'
        console.error('🚨 parseResponseToError: Body parsing failed', textError)
      }
    }

    const error = new Error(customMessage) as ParsedError
    error.name = 'ApiResponseError' // Custom name for easy identification

    // Copy properties from Response
    error.status = response?.status
    error.statusText = response?.statusText
    error.url = response?.url
    error.ok = response?.ok
    error.redirected = response?.redirected
    error.type = response?.type
    error.bodyUsed = response?.bodyUsed
    error.headers = response?.headers
      ? Object.fromEntries(response?.headers)
      : {} // Convert Headers to plain object
    error.bodyData = bodyData // Parsed body (JSON/text or null)
    error.statusCode = response?.status // Alias per existing code
    error.bodyParseError = bodyParseError || ''

    // if (bodyParseError) {
    //   error.message += ` - ${bodyParseError}`
    // }

    // if (bodyData) {
    //   error.message += ` - ${JSON.stringify(bodyData)}`
    // }

    console.log('🔄 parseResponseToError: Error parsing complete', {
      customMessage,
      error,
    })

    return error
  } catch (e) {
    console.error('🚨 parseResponseToError: Error parsing response', e)
    return new Error('An Unknown Error Occurred') as ParsedError
  }
}
